import { useCallback, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { FormProvider, useForm } from 'react-hook-form'
import { useQueryClient } from '@tanstack/react-query'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'react-toastify'
import to from 'await-to-js'
import axios from 'axios'
import { MobileModal } from 'components/UI/Modal/MobileModal'
import { Alert } from 'components/FbUI/Alert'
import { Button, ProgressBar } from '@firstbite/ui'
import useApiService from 'services/api'
import { handleFormError } from 'utils/handleFormError'
import { useMountEffect } from 'hooks/useMountEffect'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { buildContactPrefillDefaults } from 'utils/contactPrefill'
import { ContactCardPrefill, TranscriptAnalysis } from 'models/contacts'
import { ContactCompanySearchResult, DoorSearchResult } from 'models/companies'
import {
  ContactDefaultValues,
  getNewContactFormShape,
} from 'components/Modals/ContactModal/ContactModal'
import { stepsForMode, totalSteps, WizardMode } from './wizardSteps'
import ChooserStepBody from './ChooserStepBody'
import SpeakStepBody from './SpeakStepBody'
import SnapStepBody from './SnapStepBody'
import ContactInfoStep from './ContactInfoStep'
import SelectCompanyStep, { useResolveForCompany } from './SelectCompanyStep'

// Contact-info fields validated when leaving the contact step (company is
// validated separately at Submit).
const CONTACT_FIELDS: (keyof ContactDefaultValues)[] = [
  'first_name',
  'last_name',
  'email_work',
  'email_personal',
  'email_generic',
  'phone_number',
  'url',
  'linkedin_url',
]

/** Pull a human-readable message out of a create-contact failure. */
function extractCreateError(err: unknown): string {
  const fallback = 'Could not create the contact. Please try again.'
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as
      | { error_message?: string; detail?: string }
      | undefined
    return data?.error_message ?? data?.detail ?? err.message ?? fallback
  }
  return fallback
}

export default function MobileContactWizard() {
  const navigate = useNavigate()
  const api = useApiService()
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()
  const { enableMobileContactAiPrefill, isLoading: flagsLoading } =
    useFeatureFlagService()
  // The Speak / Take-a-photo options (AI prefill) are gated behind
  // enable_mobile_contact_ai_prefill. With the flag off, the chooser has
  // nothing to offer beyond manual entry, so it's skipped (see the gate below).
  const chooserDisabled = !flagsLoading && !enableMobileContactAiPrefill

  const [mode, setMode] = useState<WizardMode | undefined>()
  const [pendingMode, setPendingMode] = useState<WizardMode | undefined>()
  const [stepIndex, setStepIndex] = useState(0)
  const [prefill, setPrefill] = useState<ContactCardPrefill | undefined>()
  // Latest analysis reported by the speak step; drives the footer Next there.
  const [speakPrefill, setSpeakPrefill] = useState<
    TranscriptAnalysis | undefined
  >()
  // File chosen in the snap step; the footer Next extracts it then advances.
  const [snapFile, setSnapFile] = useState<File | undefined>()
  const [isExtracting, setIsExtracting] = useState(false)
  // Top-of-modal submit error (create failures). Shown as a centered Alert so it
  // isn't hidden behind the full-screen modal like a bottom-right toast would be.
  const [submitError, setSubmitError] = useState<string | undefined>()
  const [selectedResult, setSelectedResult] = useState<
    ContactCompanySearchResult | DoorSearchResult | undefined
  >()

  const forCompany = useResolveForCompany(selectedResult)

  const newContactFormShape = useMemo(() => getNewContactFormShape(), [])

  const defaultValues = useCallback(
    (): ContactDefaultValues =>
      ({
        first_name: '',
        last_name: '',
        // Consent is no longer collected in the UI, but the backend still
        // requires consent_to_contact=true (Contact model's validate_true
        // validator). Submit it as true, matching desktop ContactModal.
        consent_to_contact: true,
        url: '',
        linkedin_url: '',
        notes: '',
        title: '',
        seniority: undefined,
        department: undefined,
        tags: [],
        contact_company: {} as ContactDefaultValues['contact_company'],
        company_domain: '',
        email_work: '',
        email_personal: '',
        email_generic: '',
        phone_number: '',
        extension: '',
        apply_to: 'entire_company',
        door_ids: [],
      }) as ContactDefaultValues,
    []
  )

  const formMethods = useForm({
    defaultValues: defaultValues(),
    mode: 'onChange',
    resolver: zodResolver(newContactFormShape),
    reValidateMode: 'onChange',
    criteriaMode: 'all',
    shouldUnregister: false,
  })
  const { handleSubmit, getValues, reset, setValue, setError, trigger } =
    formMethods
  const { isSubmitting } = formMethods.formState

  // Deep link: /contacts/create?chain_id=123 → manual mode, pre-targeted company.
  useMountEffect(() => {
    const chainParam = searchParams.get('chain_id')
    const chainId = chainParam ? Number(chainParam) : NaN
    // Ignore non-numeric ?chain_id=abc so we never pass NaN to useGetChain.
    if (Number.isInteger(chainId) && chainId > 0) {
      setMode('manual')
      setStepIndex(0)
      // Synthetic chain result so useResolveForCompany resolves the company.
      setSelectedResult({
        item_id: chainId,
      } as ContactCompanySearchResult)
    }
  })

  // Flag gate: with the chooser disabled, skip it and start directly in manual
  // mode (there's no separate manual route — the wizard owns that flow). Setting
  // state during render is React's supported "adjust state when a value changes"
  // pattern; it converges because `mode` is no longer undefined afterward.
  if (mode === undefined && chooserDisabled) {
    setMode('manual')
  }

  const steps = mode ? stepsForMode(mode) : []
  const step = mode ? steps[stepIndex] : 'chooser'

  // Stepper progress: the chooser is pre-step (no count); once a mode is chosen
  // the bar fills by step number over the mode's total.
  const stepCount = mode ? totalSteps(mode) : 0
  const stepNum = stepIndex + 1
  const progressPct = stepCount ? (stepNum / stepCount) * 100 : 0

  const headerTitle =
    step === 'chooser'
      ? 'How do you want to start?'
      : step === 'capture'
        ? mode === 'speak'
          ? 'Speak it'
          : 'Scan a contact'
        : step === 'contact'
          ? 'Contact details'
          : 'Choose a company'

  const headerSubtitle =
    step === 'capture'
      ? mode === 'speak'
        ? 'Describe who you met — name, company, how to reach them.'
        : 'Snap a business card, event badge, or handwritten note. We will figure out the rest.'
      : step === 'contact'
        ? "Enter the contact's information."
        : step === 'company'
          ? 'Contacts match to companies based on their domain.'
          : undefined

  // Capture results are tied to a fresh visit of the capture step; clear them
  // whenever we (re)enter or leave it so a remounted body and the footer agree.
  const clearCapture = () => {
    setSpeakPrefill(undefined)
    setSnapFile(undefined)
  }

  const goBack = () => {
    // Exit to /contacts rather than navigate(-1) — the company step writes search
    // params to the URL, so "back" can land mid-search inside the wizard.
    if (!mode) {
      navigate('/contacts')
      return
    }
    clearCapture()
    if (stepIndex === 0) {
      // No chooser to return to when AI prefill is gated off — exit the wizard.
      if (chooserDisabled) {
        navigate('/contacts')
        return
      }
      setMode(undefined)
      return
    }
    setStepIndex((i) => i - 1)
  }

  // Cancel/close exits to the contacts list directly rather than navigate(-1):
  // the company step writes search params into the URL, so going "back" would
  // just unwind a search change and leave the user stuck in the wizard.
  const handleClose = () => navigate('/contacts')

  const handleChooserNext = () => {
    if (!pendingMode) return
    clearCapture()
    setMode(pendingMode)
    setStepIndex(0)
  }

  const isMountedRef = useRef(true)
  useMountEffect(() => () => {
    isMountedRef.current = false
  })

  const handleSnapNext = async () => {
    if (!snapFile) return
    setIsExtracting(true)
    const [err, result] = await to(api.captureContactFromImage(snapFile))
    if (!isMountedRef.current) return
    setIsExtracting(false)
    if (err || !result) {
      toast.error('Could not read the card. Enter the details manually.')
      handlePrefill()
      return
    }
    handlePrefill(result)
  }

  const handlePrefill = (p?: ContactCardPrefill) => {
    setPrefill(p)
    reset({ ...getValues(), ...buildContactPrefillDefaults(p) })
    setStepIndex((i) => i + 1)
  }

  const handleNextFromContact = async () => {
    const valid = await trigger(CONTACT_FIELDS)
    if (valid) setStepIndex((i) => i + 1)
  }

  const submitApiAction = async (values: ContactDefaultValues) => {
    const transformedEmails = {
      email_work: values.email_work === '' ? null : values.email_work,
      email_personal:
        values.email_personal === '' ? null : values.email_personal,
      email_generic: values.email_generic === '' ? null : values.email_generic,
    }
    const transformedValues: ContactDefaultValues = {
      ...values,
      ...transformedEmails,
    }
    return api.createContact(
      {
        ...transformedValues,
        contact_company: {
          ...transformedValues.contact_company,
          domain: transformedValues.company_domain,
        },
      },
      // Suppress the default bottom-right error toast — the wizard surfaces
      // failures inline at the top of the modal instead (see submitError).
      { disableNotifyError: true, disableNotifyGenericError: true }
    )
  }

  const handleCreate = async () => {
    if (!forCompany) return
    setSubmitError(undefined)
    setValue('contact_company', {
      id: forCompany.id,
      name: forCompany.name,
      domain: forCompany.domain,
    } as ContactDefaultValues['contact_company'])
    setValue('company_domain', forCompany.domain ?? '')
    await handleSubmit(async (values) => {
      const [err] = await to(submitApiAction(values))
      if (err) {
        handleFormError(err, setError)
        setSubmitError(extractCreateError(err))
        return
      }
      toast.success('Contact created successfully')
      // Refetch the mobile contact list + header counts so the new contact
      // shows immediately. refetchType: 'all' is required because the app sets
      // refetchOnMount: false globally and the list is inactive here (we're in
      // the wizard) — a default invalidate would only mark it stale and it
      // wouldn't refetch on navigate-back.
      void queryClient.invalidateQueries({
        queryKey: ['contacts'],
        refetchType: 'all',
      })
      void queryClient.invalidateQueries({
        queryKey: ['contacts-counts'],
        refetchType: 'all',
      })
      navigate('/contacts')
    })()
  }

  const renderFooter = () => {
    if (step === 'chooser') {
      return (
        <div className="p-2">
          <Button
            className="w-full"
            type="button"
            size="lg"
            variant="primary"
            onClick={handleChooserNext}
            disabled={!pendingMode}
          >
            Next
          </Button>
        </div>
      )
    }
    if (step === 'capture') {
      const isSpeak = mode === 'speak'
      const nextDisabled = isSpeak ? !speakPrefill : !snapFile || isExtracting
      const nextLabel = !isSpeak && isExtracting ? 'Reading…' : 'Next'
      const onNext = isSpeak
        ? () => handlePrefill(speakPrefill)
        : () => void handleSnapNext()
      return (
        <div className="flex gap-4 p-2 justify-between">
          <div className="w-1/2">
            <Button
              className="w-full"
              type="button"
              size="lg"
              variant="primary"
              appearance="outline"
              onClick={goBack}
            >
              Back
            </Button>
          </div>
          <div className="w-1/2">
            <Button
              className="w-full"
              type="button"
              size="lg"
              variant="primary"
              onClick={onNext}
              disabled={nextDisabled}
            >
              {nextLabel}
            </Button>
          </div>
        </div>
      )
    }
    if (step === 'contact') {
      return (
        <div className="flex gap-4 p-2 justify-between">
          <div className="w-1/2">
            <Button
              className="w-full"
              type="button"
              size="lg"
              variant="primary"
              appearance="outline"
              onClick={goBack}
            >
              Back
            </Button>
          </div>
          <div className="w-1/2">
            <Button
              className="w-full"
              type="button"
              size="lg"
              variant="primary"
              onClick={() => void handleNextFromContact()}
            >
              Next
            </Button>
          </div>
        </div>
      )
    }
    if (step === 'company') {
      return (
        <div className="flex gap-4 p-2 justify-between">
          <div className="w-1/2">
            <Button
              className="w-full"
              type="button"
              size="lg"
              variant="primary"
              appearance="outline"
              onClick={goBack}
            >
              Back
            </Button>
          </div>
          <div className="w-1/2">
            <Button
              className="w-full"
              type="button"
              size="lg"
              variant="primary"
              onClick={() => void handleCreate()}
              disabled={!forCompany || isSubmitting}
            >
              {isSubmitting ? 'Creating...' : 'Create Contact'}
            </Button>
          </div>
        </div>
      )
    }
    return <div />
  }

  // Before flags resolve we don't yet know whether the chooser applies; render
  // nothing rather than flash it (or the wrong first step).
  if (mode === undefined && flagsLoading) {
    return null
  }

  return (
    <MobileModal
      fullScreen
      onCancel={handleClose}
      header={
        <div className="p-2">
          {/* Cancel lives in the green top bar (MobileModal onCancel), so the
              header just carries the stepper + "Step X of Y" and the title
              block. The chooser (no mode) has no progress, so no stepper row. */}
          {mode && (
            <div className="mb-3 flex items-center gap-3">
              <ProgressBar value={progressPct} className="flex-1" />
              <span className="shrink-0 whitespace-nowrap text-xs text-fg-tertiary">
                Step {stepNum} of {stepCount}
              </span>
            </div>
          )}
          <div className="text-xs uppercase tracking-wide text-fg-tertiary mb-1">
            Create a contact
          </div>
          <h1 className="text-2xl font-semibold text-fg">{headerTitle}</h1>
          {headerSubtitle && (
            <div className="text-xs text-fg-tertiary mt-2">
              {headerSubtitle}
            </div>
          )}
        </div>
      }
      footer={renderFooter()}
    >
      {submitError && (
        <div className="p-2">
          <Alert
            color="danger"
            centered
            description={submitError}
            onDismiss={() => setSubmitError(undefined)}
          />
        </div>
      )}
      {step === 'chooser' && (
        <ChooserStepBody selectedMode={pendingMode} onSelect={setPendingMode} />
      )}
      {step === 'capture' &&
        (mode === 'speak' ? (
          <SpeakStepBody onPrefillChange={setSpeakPrefill} />
        ) : (
          <SnapStepBody onFileChange={setSnapFile} />
        ))}
      {step === 'contact' && (
        <FormProvider {...formMethods}>
          <ContactInfoStep />
        </FormProvider>
      )}
      {step === 'company' && (
        <SelectCompanyStep
          initialQuery={prefill?.company_name}
          selectedResult={selectedResult}
          onSelectResult={setSelectedResult}
        />
      )}
    </MobileModal>
  )
}
